import { useCallback, useEffect, useState } from "react";
import { api, ApiError, type Folga } from "../api";
import ErrorState from "./ErrorState";
import { formatDate } from "../format";
import { IconClose } from "../icons";

/** Pílula de status da folga. */
const STATUS_PILL: Record<string, string> = {
  Pendente: "pill-warn",
  Aprovada: "pill-ok",
  Reprovada: "pill-danger",
};

/**
 * Avaliar Folga (Operacional). Lista as folgas marcadas na agenda (cor "Folga")
 * e permite APROVAR ou REPROVAR cada uma. Ao reprovar, um popup exige o motivo.
 * A agenda é compartilhada com o Planejamento; a decisão volta pra mesma base.
 */
export default function AvaliarFolgaView() {
  const [rows, setRows] = useState<Folga[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [saving, setSaving] = useState<number | null>(null); // id sendo processado
  const [reprovar, setReprovar] = useState<Folga | null>(null); // folga no popup

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await api.folgas.list());
    } catch (err) {
      setError(err instanceof ApiError ? err : new ApiError(String(err)));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function aprovar(f: Folga) {
    setSaving(f.id);
    try {
      await api.folgas.avaliar(f.id, "Aprovada");
      await load();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : "Erro ao aprovar a folga.");
    } finally {
      setSaving(null);
    }
  }

  // Chamado pelo popup; propaga o erro para o modal exibir a mensagem.
  async function confirmarReprovar(motivo: string) {
    if (!reprovar) return;
    await api.folgas.avaliar(reprovar.id, "Reprovada", motivo);
    setReprovar(null);
    await load();
  }

  if (error) return <ErrorState message={error.message} code={error.code} onRetry={load} />;

  const pendentes = rows.filter((r) => !r.folga_status).length;

  return (
    <div className="folgas">
      <div className="folgas-head">
        <span className="muted">
          {loading
            ? "Carregando..."
            : pendentes > 0
              ? `${pendentes} folga(s) aguardando avaliação`
              : "Nenhuma folga pendente de avaliação"}
        </span>
      </div>

      <div className="table-wrap">
        {loading ? (
          <div className="empty">Carregando folgas...</div>
        ) : rows.length === 0 ? (
          <div className="empty">
            Nenhuma folga registrada na agenda. Marque uma folga (cor "Folga") na
            agenda do Planejamento para avaliá-la aqui.
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Colaborador</th>
                <th>Data</th>
                <th>Descrição</th>
                <th>Status</th>
                <th>Avaliação</th>
                <th className="col-actions">Ações</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((f) => {
                const status = f.folga_status ?? "Pendente";
                return (
                  <tr key={f.id}>
                    <td>{f.colaborador_nome ?? "—"}</td>
                    <td>{formatDate(f.data)}</td>
                    <td>{f.servico || "Folga"}</td>
                    <td>
                      <span className={`pill ${STATUS_PILL[status] ?? "pill-neutral"}`}>{status}</span>
                    </td>
                    <td>
                      {f.folga_status ? (
                        <div className="folga-aval">
                          {f.folga_obs && <span className="folga-motivo">{f.folga_obs}</span>}
                          <span className="muted folga-quem">
                            {f.folga_avaliador ? `por ${f.folga_avaliador}` : ""}
                            {f.folga_avaliado_em ? ` · ${formatDate(f.folga_avaliado_em)}` : ""}
                          </span>
                        </div>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td className="col-actions">
                      <div className="folga-acoes">
                        <button
                          className="btn-secondary btn-sm"
                          disabled={saving === f.id || f.folga_status === "Aprovada"}
                          onClick={() => aprovar(f)}
                        >
                          Aprovar
                        </button>
                        <button
                          className="btn-secondary btn-sm btn-danger-text"
                          disabled={saving === f.id || f.folga_status === "Reprovada"}
                          onClick={() => setReprovar(f)}
                        >
                          Reprovar
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {reprovar && (
        <ReprovarModal
          folga={reprovar}
          onCancel={() => setReprovar(null)}
          onConfirm={confirmarReprovar}
        />
      )}
    </div>
  );
}

/* -------------------------- Popup de reprovação -------------------------- */
function ReprovarModal({
  folga,
  onCancel,
  onConfirm,
}: {
  folga: Folga;
  onCancel: () => void;
  onConfirm: (motivo: string) => Promise<void>;
}) {
  const [motivo, setMotivo] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const m = motivo.trim();
    if (!m) {
      setError("Informe o motivo da reprovação da folga.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm(m);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao reprovar a folga.");
      setSubmitting(false);
    }
  }

  return (
    <div className="modal-overlay" onMouseDown={onCancel}>
      <div className="modal-card" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>Reprovar folga</h3>
          <button type="button" className="icon-btn" onClick={onCancel}>
            <IconClose size={18} />
          </button>
        </div>

        <form className="modal-body" onSubmit={submit}>
          <div className="folga-modal-info">
            <strong>{folga.colaborador_nome ?? "Colaborador"}</strong>
            <span className="muted">{formatDate(folga.data)}</span>
          </div>

          <label className="field">
            <span>
              Motivo da reprovação <span className="req">*</span>
            </span>
            <textarea
              autoFocus
              rows={4}
              value={motivo}
              placeholder="Ex.: já há serviço agendado nesse dia; equipe reduzida; etc."
              onChange={(e) => setMotivo(e.target.value)}
            />
          </label>

          {error && <div className="form-error">{error}</div>}

          <div className="modal-foot">
            <button type="button" className="btn-secondary" onClick={onCancel}>
              Cancelar
            </button>
            <button type="submit" className="btn-primary" disabled={submitting}>
              {submitting ? "Reprovando..." : "Confirmar reprovação"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
